import React from "react";
import {
  Stack,
  Box,
  IconButton,
  TextField,
  CircularProgress,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { css, Theme } from "@theme";
import { Icon } from "@components/Icon";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export type AutoStartState = {
  defaultChatPhraseIsDefault: boolean;
  conversationStarted: boolean;
  templateChanges: number;
  noTemplateCurrentlySelected: boolean;
};

interface PatientNotesAIDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onReplace: (text: string) => void;
  onInsertBelow: (text: string) => void;
  currentText: string;
  onGenerate: (userMessage: string, chatHistory: ChatMessage[]) => Promise<string>;
  isGenerating: boolean;
  // Auto-start integration
  autoStartTick?: number;
  autoStartState?: AutoStartState;
  onConversationStarted?: () => void;
  onDefaultPromptStateChange?: (isDefault: boolean) => void;
}

const DEFAULT_PROMPT = "Generate note based on template and current text";

const styles = {
  drawer: (theme: Theme) => css`
    background: white;
    border-left: 1px solid ${theme.palette.divider};
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  `,
  header: (theme: Theme) => css`
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${theme.palette.divider};
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  chatContainer: css`
    height: 400px; /* Fixed height for consistent scrolling behavior */
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  `,
  message: (isUser: boolean) => css`
    padding: 0.75rem 1rem;
    border-radius: 8px;
    background: ${isUser ? "#f5f5f5" : "#e3f2fd"};
    max-width: 85%;
    align-self: ${isUser ? "flex-end" : "flex-start"};
    word-wrap: break-word;
  `,
  actions: (theme: Theme) => css`
    padding: 0.5rem 1rem;
    border-top: 1px solid ${theme.palette.divider};
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: center;
    align-items: center;
  `,
  inputContainer: css`
    padding: 1rem;
    border-top: 1px solid #e0e0e0;
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
  `,
};

export const PatientNotesAIDrawer: React.FC<PatientNotesAIDrawerProps> = ({
  isOpen,
  onClose,
  onReplace,
  onInsertBelow,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentText,
  onGenerate,
  isGenerating,
  autoStartTick,
  autoStartState,
  onConversationStarted,
  onDefaultPromptStateChange,
}) => {
  const [chatHistory, setChatHistory] = React.useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = React.useState(DEFAULT_PROMPT);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  const latestAssistantMessage = React.useMemo(() => {
    const assistantMessages = chatHistory.filter((m) => m.role === "assistant");
    return assistantMessages.length > 0
      ? assistantMessages[assistantMessages.length - 1].content
      : "";
  }, [chatHistory]);

  React.useEffect(() => {
    // Only scroll if there are messages, and do it after a brief delay to prevent parent scroll
    if (chatHistory.length > 0) {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);
    }
  }, [chatHistory]);

  React.useEffect(() => {
    if (!isOpen) {
      // Reset chat when drawer closes
      setChatHistory([]);
      setInputValue(DEFAULT_PROMPT);
    }
  }, [isOpen]);

  // Notify parent whether the prompt is still the default
  const isDefaultPrompt = inputValue === DEFAULT_PROMPT;
  React.useEffect(() => {
    if (onDefaultPromptStateChange) {
      onDefaultPromptStateChange(isDefaultPrompt);
    }
  }, [isDefaultPrompt, onDefaultPromptStateChange]);

  const handleSend = React.useCallback(async () => {
    if (!inputValue.trim() || isGenerating) return;

    const userMessage: ChatMessage = {
      role: "user",
      content: inputValue,
    };

    const newHistory = [...chatHistory, userMessage];
    setChatHistory(newHistory);
    // Mark conversation started immediately upon sending first user message
    if (onConversationStarted && chatHistory.length === 0) {
      onConversationStarted();
    }
    setInputValue("");

    try {
      // Pass the updated history (including the new user message) to onGenerate
      const response = await onGenerate(inputValue, newHistory);
      setChatHistory([...newHistory, { role: "assistant", content: response }]);
    } catch (error) {
      // Error handling is done in the parent component
    }
  }, [inputValue, chatHistory, onGenerate, isGenerating, onConversationStarted]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        handleSend();
        return false;
      }
    },
    [handleSend]
  );

  const handleReplace = React.useCallback(() => {
    if (latestAssistantMessage) {
      onReplace(latestAssistantMessage);
    }
  }, [latestAssistantMessage, onReplace]);

  const handleInsert = React.useCallback(() => {
    if (latestAssistantMessage) {
      onInsertBelow(latestAssistantMessage);
    }
  }, [latestAssistantMessage, onInsertBelow]);

  const handleCopy = React.useCallback(() => {
    if (latestAssistantMessage) {
      navigator.clipboard.writeText(latestAssistantMessage);
    }
  }, [latestAssistantMessage]);

  // Auto-start on tick when conditions are satisfied
  const basicReady = React.useMemo(() => (
    Boolean(
      autoStartTick &&
      isOpen &&
      !isGenerating &&
      chatHistory.length === 0 &&
      inputValue === DEFAULT_PROMPT
    )
  ), [autoStartTick, isOpen, isGenerating, chatHistory.length, inputValue]);

  const stateReady = React.useMemo(() => (
    !autoStartState || (
      autoStartState.defaultChatPhraseIsDefault &&
      !autoStartState.conversationStarted &&
      !autoStartState.noTemplateCurrentlySelected &&
      autoStartState.templateChanges >= 1
    )
  ), [autoStartState]);

  React.useEffect(() => {
    if (basicReady && stateReady) {
      void handleSend();
    }
  }, [basicReady, stateReady, handleSend]);

  if (!isOpen) return null;

  return (
    <Box css={styles.drawer}>

      <Box css={styles.header}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Icon icon="build" width={20} />
          <Typography variant="subtitle1">AI Assistant</Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ width: 20, height: 20 }} />
        </IconButton>
      </Box>

      <Box css={styles.chatContainer}>
        {chatHistory.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" flex={1} gap={2}>
            <ChatIcon sx={{ fontSize: 48, color: "#999" }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Ask the AI to help write or improve your notes.
              <br />
              Start a conversation below.
            </Typography>
          </Stack>
        ) : (
          <>
            {chatHistory.map((msg, idx) => (
              <Box key={idx} css={styles.message(msg.role === "user")}>
                <Typography
                  variant="body2"
                  style={{ whiteSpace: "pre-wrap", margin: 0 }}
                >
                  {msg.content}
                </Typography>
              </Box>
            ))}
            {isGenerating && (
              <Box css={styles.message(false)}>
                <CircularProgress size={20} />
              </Box>
            )}
            <div ref={chatEndRef} />
          </>
        )}
      </Box>

      {(latestAssistantMessage && !isGenerating) && (
        <Box css={styles.actions}>
          <Tooltip title="Replace with this answer">
            <IconButton
              onClick={handleReplace}
              size="medium"
              sx={{
                backgroundColor: "primary.light",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert below">
            <IconButton
              onClick={handleInsert}
              size="medium"
              sx={{
                backgroundColor: "primary.light",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
              }}
            >
              <ArrowDownIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton
              onClick={handleCopy}
              size="medium"
              sx={{
                backgroundColor: "primary.light",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.main",
                },
              }}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        css={styles.inputContainer}
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.nativeEvent) {
            e.nativeEvent.stopImmediatePropagation();
          }
          return false;
        }}
      >
        <TextField
          multiline
          maxRows={4}
          fullWidth
          size="small"
          placeholder="Ask assistant to edit or write..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isGenerating}
          sx={{
            "& .MuiInputBase-root": {
              paddingRight: "8px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: "center", mb: 0.5 }}>
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isGenerating}
                  edge="end"
                  size="small"
                  type="button"
                  sx={{ mt: 0.5 }}
                >
                  <SendIcon sx={{ width: 20, height: 20 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

